/**
 * Word Document Generator for Seance
 * 
 * This utility generates a Word document (.docx) from seance data.
 * Uses the 'docx' library to create properly formatted documents.
 */

/**
 * Generates a Word document from seance data
 * @param {Object} seance - The seance object containing sequences and blocks
 * @returns {Promise<Blob>} - A blob containing the Word document
 */
export async function generateWordDocument(seance) {
  // Dynamic import to avoid issues if library is not installed
  let docxModule;
  try {
    docxModule = await import('docx');
  } catch (error) {
    throw new Error(
      'La bibliothèque docx n\'est pas installée. Veuillez exécuter: npm install docx file-saver'
    );
  }
  
  const { Document, Packer, Paragraph, TextRun, HeadingLevel, AlignmentType, ImageRun, WidthType, HeightType } = docxModule;
  
  if (!seance) {
    throw new Error('Seance data is required');
  }

  const docChildren = [];

  // Title
  docChildren.push(
    new Paragraph({
      text: seance.title || 'Séance sans titre',
      heading: HeadingLevel.TITLE,
      alignment: AlignmentType.CENTER,
      spacing: { after: 400 }
    })
  );

  // Metadata section
  const metadata = [];
  if (seance.theme) metadata.push(`Sujet: ${seance.theme}`);
  if (seance.age) metadata.push(`Âge: ${seance.age}`);
  if (seance.level) metadata.push(`Niveau: ${formatLevel(seance.level)}`);
  if (seance.language) metadata.push(`Langue: ${seance.language}`);
  if (seance.created_at) metadata.push(`Créé le: ${formatDate(seance.created_at)}`);
  
  if (metadata.length > 0) {
    docChildren.push(
      new Paragraph({
        text: metadata.join(' | '),
        alignment: AlignmentType.CENTER,
        spacing: { after: 600 }
      })
    );
  }

  // Add a horizontal line (represented by a paragraph with dashes)
  docChildren.push(
    new Paragraph({
      text: '─────────────────────────────────────────────────────────────',
      alignment: AlignmentType.CENTER,
      spacing: { after: 600 }
    })
  );

      // Iterate through sequences
      if (seance.sequences && seance.sequences.length > 0) {
        for (let sequenceIndex = 0; sequenceIndex < seance.sequences.length; sequenceIndex++) {
          const sequence = seance.sequences[sequenceIndex];
          // Sequence title (use subTheme from API)
          docChildren.push(
            new Paragraph({
              text: `Séquence ${sequenceIndex + 1}: ${sequence.subTheme || `Séquence ${sequenceIndex + 1}`}`,
              heading: HeadingLevel.HEADING_1,
              spacing: { before: 600, after: 300 }
            })
          );

          // Process blocks (use blocs from API)
          const blocks = sequence.blocs || [];
      
      if (blocks.length === 0) {
        docChildren.push(
          new Paragraph({
            text: 'Aucun bloc dans cette séquence.',
            italics: true,
            spacing: { after: 400 }
          })
        );
      }

      for (let blockIndex = 0; blockIndex < blocks.length; blockIndex++) {
        const block = blocks[blockIndex];
        const blockType = block.type_bloc || 'unknown';
        
        // Block type header
        docChildren.push(
          new Paragraph({
            text: `Bloc ${blockIndex + 1}: ${formatBlockType(blockType)}`,
            heading: HeadingLevel.HEADING_2,
            spacing: { before: 400, after: 200 }
          })
        );

        // Process block content based on type
        switch (blockType) {
          case 'image':
            await processImageBlock(block, docChildren, Paragraph, TextRun, ImageRun, WidthType, HeightType, AlignmentType);
            break;
          case 'teacher_notes':
            await processTeacherNotesBlock(block, docChildren, Paragraph, TextRun, ImageRun, WidthType, HeightType, AlignmentType);
            break;
          case 'quiz':
            await processQuizBlock(block, docChildren, Paragraph, TextRun, ImageRun, WidthType, HeightType, AlignmentType);
            break;
          default:
            docChildren.push(
              new Paragraph({
                text: `Type de bloc non reconnu: ${blockType}`,
                italics: true,
                spacing: { after: 200 }
              })
            );
        }

        // Add spacing after block
        docChildren.push(
          new Paragraph({
            text: '',
            spacing: { after: 300 }
          })
        );
      }

      // Add page break between sequences (except for the last one)
      if (sequenceIndex < seance.sequences.length - 1) {
        docChildren.push(
          new Paragraph({
            text: '',
            spacing: { after: 400 }
          })
        );
      }
    }
  } else {
    docChildren.push(
      new Paragraph({
        text: 'Aucune séquence dans cette séance.',
        italics: true
      })
    );
  }

  // Create the document
  const doc = new Document({
    sections: [{
      properties: {},
      children: docChildren
    }]
  });

  // Generate the blob
  return Packer.toBlob(doc);
}

/**
 * Download image from URL and convert to ArrayBuffer
 * Returns both the ArrayBuffer and the image type (png, jpeg, etc.)
 */
async function downloadImageAsArrayBuffer(imageUrl) {
  try {
    const response = await fetch(imageUrl);
    if (!response.ok) {
      throw new Error(`Failed to fetch image: ${response.statusText}`);
    }
    const blob = await response.blob();
    const arrayBuffer = await blob.arrayBuffer();
    
    // Detect image type from blob or URL
    let imageType = 'png'; // default
    const contentType = blob.type;
    if (contentType) {
      if (contentType.includes('jpeg') || contentType.includes('jpg')) {
        imageType = 'jpeg';
      } else if (contentType.includes('png')) {
        imageType = 'png';
      } else if (contentType.includes('gif')) {
        imageType = 'gif';
      } else if (contentType.includes('webp')) {
        imageType = 'png'; // WebP not directly supported, convert to png
      }
    } else {
      // Fallback: check URL extension
      const urlLower = imageUrl.toLowerCase();
      if (urlLower.includes('.jpg') || urlLower.includes('.jpeg')) {
        imageType = 'jpeg';
      } else if (urlLower.includes('.png')) {
        imageType = 'png';
      } else if (urlLower.includes('.gif')) {
        imageType = 'gif';
      }
    }
    
    return { buffer: arrayBuffer, type: imageType };
  } catch (error) {
    console.error('Error downloading image:', error);
    throw error;
  }
}

/**
 * Process image block content
 */
async function processImageBlock(block, docChildren, Paragraph, TextRun, ImageRun, WidthType, HeightType, AlignmentType) {
  const allImages = block.bloc_data?.images || block.content?.images || [];
  // Filter to only include validated images
  const images = allImages.filter(img => img.status_seance === 'validated');
  
  if (images.length === 0) {
    docChildren.push(
      new Paragraph({
        text: 'Aucune image validée dans ce bloc.',
        italics: true
      })
    );
    return;
  }

  for (let imgIndex = 0; imgIndex < images.length; imgIndex++) {
    const image = images[imgIndex];
    const imageUrl = image.img_url;
    
    if (!imageUrl || typeof imageUrl !== 'string') {
      docChildren.push(
        new Paragraph({
          children: [
            new TextRun({
              text: `  ${imgIndex + 1}. URL non disponible`,
              bold: true
            })
          ],
          spacing: { after: 100 }
        })
      );
      continue;
    }

    try {
      // Download image and convert to ArrayBuffer
      const { buffer: imageBuffer, type: imageType } = await downloadImageAsArrayBuffer(imageUrl);
      
      // Create image run - width of 400 points (approximately 14cm)
      const imageRun = new ImageRun({
        data: imageBuffer,
        transformation: {
          width: 400,
          height: 300,
        },
        type: imageType
      });

      docChildren.push(
        new Paragraph({
          children: [
            new TextRun({
              text: `Image ${imgIndex + 1}: `,
              bold: true
            }),
            imageRun
          ],
          spacing: { after: 200 },
          alignment: AlignmentType.CENTER
        })
      );
    } catch (error) {
      console.error(`Error processing image ${imgIndex + 1}:`, error);
      // Fallback to URL if image download fails
      docChildren.push(
        new Paragraph({
          children: [
            new TextRun({
              text: `  ${imgIndex + 1}. `,
              bold: true
            }),
            new TextRun({
              text: imageUrl,
              color: '0066CC'
            }),
            new TextRun({
              text: ' (Erreur lors du téléchargement de l\'image)',
              italics: true,
              color: 'FF0000'
            })
          ],
          spacing: { after: 100 }
        })
      );
    }
  }
}

/**
 * Process teacher notes block content
 */
async function processTeacherNotesBlock(block, docChildren, Paragraph, TextRun, ImageRun, WidthType, HeightType, AlignmentType) {
  const notes = block.bloc_data?.notes || block.layers || block.content?.notes || [];
  const allImages = block.bloc_data?.images || [];
  // Filter to only include validated images
  const images = allImages.filter(img => img.status_seance === 'validated');
  
  if (notes.length === 0 && images.length === 0) {
    docChildren.push(
      new Paragraph({
        text: 'Aucune note dans ce bloc.',
        italics: true
      })
    );
    return;
  }

  // Process notes
  for (let noteIndex = 0; noteIndex < notes.length; noteIndex++) {
    const note = notes[noteIndex];
    const noteTitle = note.title || `Note ${noteIndex + 1}`;
    const noteContent = note.content?.text || note.content || note.text || '';
    
    docChildren.push(
      new Paragraph({
        children: [
          new TextRun({
            text: `${noteTitle}:`,
            bold: true
          })
        ],
        spacing: { after: 100 }
      })
    );

    if (noteContent) {
      docChildren.push(
        new Paragraph({
          text: noteContent,
          spacing: { after: 200 }
        })
      );
    } else {
      docChildren.push(
        new Paragraph({
          text: '(Contenu vide)',
          italics: true,
          spacing: { after: 200 }
        })
      );
    }
  }

  // Process images if any (only validated images)
  if (images.length > 0) {
    for (let imgIndex = 0; imgIndex < images.length; imgIndex++) {
      const image = images[imgIndex];
      const imageUrl = image.img_url;
      
      if (!imageUrl || typeof imageUrl !== 'string') {
        continue;
      }

      try {
        const { buffer: imageBuffer, type: imageType } = await downloadImageAsArrayBuffer(imageUrl);
        const imageRun = new ImageRun({
          data: imageBuffer,
          transformation: {
            width: 400,
            height: 300,
          },
          type: imageType
        });

        docChildren.push(
          new Paragraph({
            children: [imageRun],
            spacing: { after: 200 },
            alignment: AlignmentType.CENTER
          })
        );
      } catch (error) {
        console.error(`Error processing image ${imgIndex + 1} in teacher notes:`, error);
      }
    }
  }
}

/**
 * Process quiz block content
 */
async function processQuizBlock(block, docChildren, Paragraph, TextRun, ImageRun, WidthType, HeightType, AlignmentType) {
  const question = block.bloc_data?.question || 
                   block.content?.question?.text || 
                   block.content?.question || 
                   '';
  
  const answers = block.bloc_data?.answers || 
                  block.content?.answers || 
                  [];

  const allImages = block.bloc_data?.images || [];
  // Filter to only include validated images
  const images = allImages.filter(img => img.status_seance === 'validated');

  if (!question && answers.length === 0 && images.length === 0) {
    docChildren.push(
      new Paragraph({
        text: 'Aucun contenu de quiz dans ce bloc.',
        italics: true
      })
    );
    return;
  }

  // Question
  if (question) {
    docChildren.push(
      new Paragraph({
        children: [
          new TextRun({
            text: 'Question: ',
            bold: true
          }),
          new TextRun({
            text: question
          })
        ],
        spacing: { after: 200 }
      })
    );
  }

  // Answers
  if (answers.length > 0) {
    docChildren.push(
      new Paragraph({
        text: 'Réponses:',
        bold: true,
        spacing: { before: 100, after: 100 }
      })
    );

    answers.forEach((answer, ansIndex) => {
      const answerText = answer.answer || answer.text || '';
      const isCorrect = answer.outcome === 'good answer' || 
                       answer.is_correct || 
                       false;
      const prefix = String.fromCharCode(65 + ansIndex); // A, B, C, D...
      
      docChildren.push(
        new Paragraph({
          children: [
            new TextRun({
              text: `${prefix}. `,
              bold: true
            }),
            new TextRun({
              text: answerText
            }),
            new TextRun({
              text: isCorrect ? ' ✓ (Correct)' : '',
              bold: isCorrect,
              color: isCorrect ? '00AA00' : undefined
            })
          ],
          spacing: { after: 100 }
        })
      );
    });
  }

  // Process images if any (only validated images)
  if (images.length > 0) {
    for (let imgIndex = 0; imgIndex < images.length; imgIndex++) {
      const image = images[imgIndex];
      const imageUrl = image.img_url;
      
      if (!imageUrl || typeof imageUrl !== 'string') {
        continue;
      }

      try {
        const { buffer: imageBuffer, type: imageType } = await downloadImageAsArrayBuffer(imageUrl);
        const imageRun = new ImageRun({
          data: imageBuffer,
          transformation: {
            width: 400,
            height: 300,
          },
          type: imageType
        });

        docChildren.push(
          new Paragraph({
            children: [imageRun],
            spacing: { after: 200 },
            alignment: AlignmentType.CENTER
          })
        );
      } catch (error) {
        console.error(`Error processing image ${imgIndex + 1} in quiz:`, error);
      }
    }
  }
}

/**
 * Format block type name
 */
function formatBlockType(blockType) {
  const typeMap = {
    'image': '🖼️ Image',
    'teacher_notes': '📝 Notes du professeur',
    'quiz': '❓ Quiz',
    'citation': '💬 Citation',
    'practical_activity': '🎯 Activité pratique',
    'free_text': '📄 Texte libre'
  };
  return typeMap[blockType] || blockType;
}

/**
 * Format level name
 */
function formatLevel(level) {
  const levelMap = {
    'easy': 'Débutant',
    'beginner': 'Débutant',
    'intermediate': 'Intermédiaire',
    'advanced': 'Avancé'
  };
  return levelMap[level] || level;
}

/**
 * Format date string
 */
function formatDate(dateString) {
  if (!dateString) return '';
  try {
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  } catch (e) {
    return dateString;
  }
}

/**
 * Downloads the Word document
 * @param {Blob} blob - The document blob
 * @param {string} filename - The filename for the download
 */
export async function downloadWordDocument(blob, filename = 'seance.docx') {
  // Try to use file-saver if available, otherwise fallback to basic download
  try {
    const { saveAs } = await import('file-saver');
    saveAs(blob, filename);
  } catch (error) {
    console.warn('file-saver not available, using fallback download method');
    // Fallback download method
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }
}

